/**
 * Intake Manager column ids and labels — must stay aligned with
 * `backend/app/utils/intake_export.py` (`INTAKE_EXPORT_ROWS` + UI-only ids).
 */

/**
 * Server-side sort key: a backend `INTAKE_SORT_KEYS` key (every Intake Manager
 * column id plus legacy keys such as `created_at`). Drift-tested by
 * `backend/tests/utils/test_intake_manager_columns.py`.
 */
export type IntakeSortColumn = string;

export type IntakeManagerColumnMeta = {
  id: string;
  label: string;
  /**
   * Legacy server sort key for this column. Every column is sortable; when
   * unset, the column id itself is the sort key.
   */
  sortKey?: IntakeSortColumn;
};

/** Excel-aligned export columns (stable ids). Order matches backend export. */
export const INTAKE_MANAGER_EXPORT_COLUMN_CATALOG: IntakeManagerColumnMeta[] = [
  {
    id: 'patient_first_name',
    label: 'Patient First Name',
    sortKey: 'patient_first_name',
  },
  {
    id: 'patient_last_name',
    label: 'Patient Last Name',
    sortKey: 'patient_last_name',
  },
  { id: 'patient_middle_name', label: 'Patient Middle Name' },
  { id: 'patient_suffix', label: 'Patient Suffix' },
  { id: 'patient_dob', label: 'Patient DOB' },
  { id: 'patient_gender', label: 'Patient Gender' },
  { id: 'patient_ssn', label: 'Patient SSN' },
  { id: 'patient_phone', label: 'Patient Phone' },
  { id: 'patient_secondary_phone', label: 'Patient Secondary Phone' },
  { id: 'patient_email', label: 'Patient Email' },
  { id: 'street_address', label: 'Street Address' },
  { id: 'city', label: 'City' },
  { id: 'state', label: 'State' },
  { id: 'zip_code', label: 'Zip Code' },
  { id: 'county', label: 'County' },
  { id: 'primary_language', label: 'Primary Language' },
  { id: 'race', label: 'Race' },
  { id: 'ethnicity', label: 'Ethnicity' },
  { id: 'height', label: 'Height' },
  { id: 'weight', label: 'Weight' },
  { id: 'medicaid_id', label: 'Medicaid ID' },
  { id: 'emergency_contact_name', label: 'Emergency Contact Name' },
  {
    id: 'emergency_contact_relationship',
    label: 'Emergency Contact Relationship',
  },
  { id: 'emergency_contact_phone', label: 'Emergency Contact Phone' },
  { id: 'insurance_mapped', label: 'Insurance (Mapped)' },
  { id: 'primary_insurance', label: 'Primary Insurance' },
  { id: 'primary_member_id', label: 'Primary Member ID' },
  { id: 'secondary_insurance', label: 'Secondary Insurance' },
  { id: 'secondary_member_id', label: 'Secondary Member ID' },
  {
    id: 'referring_provider',
    label: 'Referring Provider',
    sortKey: 'referring_physician',
  },
  { id: 'referring_phone', label: 'Referring Phone' },
  { id: 'referring_fax', label: 'Referring Fax' },
  {
    id: 'referral_source',
    label: 'Referral Source',
    sortKey: 'referral_source',
  },
  { id: 'external_referral_source', label: 'External Referral Source' },
  { id: 'internal_referral_source', label: 'Internal Referral Source' },
  { id: 'discharge_facility', label: 'Discharge Facility' },
  { id: 'discharge_facility_type', label: 'Discharge Facility Type' },
  { id: 'f2f_physician', label: 'F2F Physician' },
  { id: 'primary_diagnosis', label: 'Primary Diagnosis' },
  { id: 'diagnosis_codes', label: 'Diagnosis Codes' },
  { id: 'services_requested', label: 'Services Requested' },
  { id: 'allergies', label: 'Allergies' },
  { id: 'past_medical_history', label: 'Past Medical History' },
  { id: 'homebound_reason', label: 'Homebound Reason' },
  { id: 'physician_order_date', label: 'Physician Order Date' },
  { id: 'order_attached', label: 'Order Attached' },
  { id: 'order_signed', label: 'Order Signed' },
  { id: 'f2f_encounter_date', label: 'F2F Encounter Date' },
  { id: 'f2f_attached', label: 'F2F Attached' },
  { id: 'f2f_signed', label: 'F2F Signed' },
  { id: 'extraction', label: 'Extraction' },
  { id: 'eligibility', label: 'Eligibility' },
  { id: 'prior_auth', label: 'Prior Auth' },
  { id: 'ready_to_call', label: 'Ready to Call' },
  { id: 'ready_to_call_comments', label: 'Comments' },
  { id: 'scheduling', label: 'Scheduling' },
  { id: 'push_to_emr', label: 'Push to EMR' },
  { id: 'clinic', label: 'Clinic', sortKey: 'agency_clinic_id' },
  { id: 'archived', label: 'Archived' },
  { id: 'non_admit', label: 'Non-Admit' },
  { id: 'non_admit_reason', label: 'Non-Admit Reason' },
  { id: 'non_admit_reason_details', label: 'Non-Admit Details' },
  { id: 'visit_type', label: 'Visit Type' },
  {
    id: 'visit_date',
    label: 'Visit Date',
    sortKey: 'kinnser_soc_date',
  },
  { id: 'discharge_date', label: 'Discharge Date' },
  { id: 'created', label: 'Created', sortKey: 'created_at' },
  {
    id: 'created_timestamp',
    label: 'Created Timestamp',
    sortKey: 'created_at',
  },
  { id: 'pushed_timestamp', label: 'Pushed to EMR Timestamp' },
  { id: 'time_to_push', label: 'Time to Push' },
];

/** Composite / special columns used by the default Intake Manager layout. */
export const INTAKE_MANAGER_UI_ONLY_COLUMN_CATALOG: IntakeManagerColumnMeta[] =
  [
    { id: 'patient', label: 'Patient', sortKey: 'patient_last_name' },
    { id: 'status', label: 'Status', sortKey: 'status' },
    { id: 'insurance', label: 'Insurance' },
    { id: 'assignee', label: 'Assignee', sortKey: 'assigned_to' },
    { id: 'soc_date', label: 'SOC/ROC Date', sortKey: 'kinnser_soc_date' },
  ];

export const INTAKE_MANAGER_FULL_CATALOG: IntakeManagerColumnMeta[] = [
  ...INTAKE_MANAGER_UI_ONLY_COLUMN_CATALOG,
  ...INTAKE_MANAGER_EXPORT_COLUMN_CATALOG,
];

export const INTAKE_MANAGER_ALLOWED_COLUMN_IDS: ReadonlySet<string> = new Set(
  INTAKE_MANAGER_FULL_CATALOG.map(c => c.id)
);

export const DEFAULT_INTAKE_MANAGER_COLUMNS: readonly string[] = [
  'patient',
  'status',
  'clinic',
  'insurance',
  'referring_provider',
  'referral_source',
  'assignee',
  'soc_date',
  'created',
] as const;

export function labelForIntakeManagerColumn(columnId: string): string {
  const row = INTAKE_MANAGER_FULL_CATALOG.find(c => c.id === columnId);
  return row?.label ?? columnId;
}

export function sortKeyForIntakeManagerColumn(
  columnId: string
): IntakeSortColumn | undefined {
  const row = INTAKE_MANAGER_FULL_CATALOG.find(c => c.id === columnId);
  return row ? (row.sortKey ?? row.id) : undefined;
}

export function effectiveIntakeManagerColumns(
  stored: string[] | null | undefined
): string[] {
  if (stored && stored.length > 0) {
    const filtered = stored.filter(id =>
      INTAKE_MANAGER_ALLOWED_COLUMN_IDS.has(id)
    );
    if (filtered.length > 0) return filtered;
  }
  return [...DEFAULT_INTAKE_MANAGER_COLUMNS];
}
